import { useRef, useState } from "react";
import type { MediaAsset } from "../domain/types";
import {
  MediaValidationError,
  createAndUploadMedia,
} from "../media/createMediaAsset";
import { PHOTO_ACCEPT, PHOTO_MAX, PHOTO_MIN } from "../media/limits";
import { uploadStatusLabel, uploadUserMessage } from "../media/uploadMessages";
import { useT } from "../i18n";
import { LiveStillCapture } from "./LiveStillCapture";

export function PhotoGrid({
  photos,
  userId,
  onAdd,
  onRemove,
}: {
  photos: MediaAsset[];
  userId: string;
  onAdd: (assets: MediaAsset[]) => void;
  onRemove: (id: string) => void;
}) {
  const t = useT();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const remaining = PHOTO_MAX - photos.length;
  const full = remaining <= 0;

  const ingestFiles = (list: FileList | File[] | null) => {
    if (!list || (Array.isArray(list) ? list.length === 0 : list.length === 0)) return;
    const files = Array.isArray(list) ? list : [...list];
    void (async () => {
      const ownerId = userId || "draft";
      setBusy(true);
      setError(null);
      const created: MediaAsset[] = [];
      try {
        for (const file of files.slice(0, remaining)) {
          const uploaded = await createAndUploadMedia("profile_photo", file, ownerId);
          created.push(uploaded);
        }
        if (created.some((a) => a.uploadStatus === "failed")) {
          const first = created.find((a) => a.uploadStatus === "failed");
          setError(uploadUserMessage(first, t) || t("capture.photos.someFailed"));
        } else {
          const warn = created.map((a) => uploadUserMessage(a, t)).find(Boolean);
          if (warn) setError(warn);
        }
        onAdd(created);
      } catch (e) {
        for (const a of created) {
          if (a.objectUrl.startsWith("blob:")) URL.revokeObjectURL(a.objectUrl);
        }
        setError(e instanceof MediaValidationError ? e.message : t("capture.photos.addFailed"));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <div className="photo-grid">
      <div className="photo-grid__meta">
        <span>{t("capture.photos.count", { count: photos.length, max: PHOTO_MAX })}</span>
        <span className={photos.length >= PHOTO_MIN ? "is-ok" : ""}>
          {photos.length >= PHOTO_MIN
            ? t("capture.photos.minimumMet")
            : t("capture.photos.moreRequired", { n: PHOTO_MIN - photos.length })}
        </span>
      </div>

      <ul className="photo-grid__list" aria-label={t("capture.photos.listAria")}>
        {photos.map((photo, index) => (
          <li key={photo.id} className="photo-grid__item">
            <img
              src={photo.publicUrl || photo.objectUrl}
              alt={t("capture.photos.photoAlt", { n: index + 1 })}
            />
            <span className="photo-grid__status">{uploadStatusLabel(photo, t)}</span>
            <button
              type="button"
              className="photo-grid__remove"
              aria-label={t("capture.photos.removeAria", { n: index + 1 })}
              onClick={() => onRemove(photo.id)}
            >
              ×
            </button>
          </li>
        ))}

        {!full ? (
          <li className="photo-grid__item photo-grid__item--add">
            <button
              type="button"
              className="photo-grid__add"
              disabled={busy}
              onClick={() => setCameraOpen(true)}
            >
              <span className="photo-grid__add-icon">+</span>
              <span>{busy ? t("common.ellipsis") : t("capture.photos.camera")}</span>
            </button>
          </li>
        ) : null}
      </ul>

      <div className="photo-grid__actions">
        <button
          type="button"
          className="capture__btn"
          disabled={full || busy}
          onClick={() => setCameraOpen(true)}
        >
          {busy ? t("capture.photos.uploading") : t("capture.photos.takePhoto")}
        </button>
        <button
          type="button"
          className="capture__btn capture__btn--ghost"
          disabled={full || busy}
          onClick={() => fileRef.current?.click()}
        >
          {t("capture.photos.chooseFiles")}
        </button>
      </div>

      {error ? <p className="capture__error">{error}</p> : null}

      <p className="photo-grid__hint">{t("capture.photos.hint", { max: PHOTO_MAX })}</p>

      <LiveStillCapture
        open={cameraOpen}
        facing="user"
        title={t("capture.photos.cameraTitle")}
        onClose={() => setCameraOpen(false)}
        onCapture={(file) => ingestFiles([file])}
      />

      <input
        ref={fileRef}
        className="sr-only"
        type="file"
        accept={PHOTO_ACCEPT}
        multiple
        onChange={(e) => {
          ingestFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}
